/**
 * Insert / edit / remove a link (Ctrl+K or the toolbar). Edits the link under the cursor, or
 * links the selected text; with an empty selection the display text is inserted as well.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import type { Editor } from '@tiptap/react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../components/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/form';
import { Input } from '../components/input';
import { normalizeLink } from './extensions';
import type { RichTextEditorLabels } from './labels';

export interface LinkDialogProps {
  editor: Editor;
  labels: RichTextEditorLabels;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LinkDialog({ editor, labels, open, onOpenChange }: LinkDialogProps) {
  const schema = useMemo(
    () =>
      z.object({
        href: z.string().refine((value) => normalizeLink(value) !== undefined, { message: labels.linkInvalid }),
        text: z.string(),
      }),
    [labels],
  );
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { href: '', text: '' } });
  const editing = editor.isActive('link');
  const needsText = !editing && editor.state.selection.empty;

  useEffect(() => {
    if (!open) return;
    if (editing) editor.chain().extendMarkRange('link').run();
    const { from, to } = editor.state.selection;
    form.reset({ href: (editor.getAttributes('link').href as string | undefined) ?? '', text: editor.state.doc.textBetween(from, to, ' ') });
  }, [open, editing, editor, form]);

  const close = () => {
    onOpenChange(false);
    editor.commands.focus();
  };

  const save = form.handleSubmit(({ href, text }) => {
    const url = normalizeLink(href);
    if (url === undefined) return;
    const chain = editor.chain().focus();
    if (needsText) {
      const label = text.trim() || url.replace(/^mailto:/i, '');
      chain.insertContent({ type: 'text', text: label, marks: [{ type: 'link', attrs: { href: url } }] }).run();
    } else {
      chain.extendMarkRange('link').setLink({ href: url }).run();
    }
    close();
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? labels.linkEditTitle : labels.linkInsertTitle}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            noValidate
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.stopPropagation();
              void save(event);
            }}
          >
            <FormField
              control={form.control}
              name="href"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{labels.linkUrl}</FormLabel>
                  <FormControl>
                    <Input autoFocus inputMode="url" placeholder="https://" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {needsText && (
              <FormField
                control={form.control}
                name="text"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{labels.linkText}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
            <DialogFooter>
              {editing && (
                <Button
                  type="button"
                  variant="ghost"
                  className="sm:mr-auto"
                  onClick={() => {
                    editor.chain().focus().extendMarkRange('link').unsetLink().run();
                    close();
                  }}
                >
                  {labels.linkRemove}
                </Button>
              )}
              <Button type="button" variant="outline" onClick={close}>
                {labels.cancel}
              </Button>
              <Button type="submit">{labels.linkSave}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
